import { Box, Stack, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CardsColumn,
  CardSlot,
  CardSlotInner,
  DotButton,
  DotColumn,
  NavigationWrapper
} from "./ActivityDotNavigation.styles";
import type {
  ActivityDotNavigationProps,
  ActivityNavigationItem
} from "./ActivityDotNavigation.types";
import { CourseActivityCard } from "./CourseActivityCard.component";
import { useActivityAndCourseSelection } from "../../components/DetailsEsploraRestyle/IndexActivitySelected.context";
import { LearningObjectAlternativeSection } from "../../components/LearningObjectAlternativeSection";
import type { StructuredAlternativeSectionItem } from "../../utils/learningObjectStructure";

const isAlternativeSection = (
  item: ActivityNavigationItem
): item is StructuredAlternativeSectionItem =>
  "kind" in item && item.kind === "alternative-section";

export function ActivityDotNavigation({
  activities,
  initialActiveLoId,
  courseId,
  deepLinkLanding = false,
  selectionDisabled = false
}: ActivityDotNavigationProps) {
  const theme = useTheme();

  const [advancedNavigation, setAdvancedNavigation] = useState<boolean>(false);

  const [indexFirstDot, setIndexFirstDot] = useState(0);
  const [blinkId, setBlinkId] = useState<number | null>(null);
  const landingHandled = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { activityIdSelected, setActivityIdSelected, syncUrlToActivity } = useActivityAndCourseSelection();

  const maxActivitiesVisible = 8;

  const maxStart = activities.length - maxActivitiesVisible;
  const indexesDotList = [
    indexFirstDot - 1,
    ...Array.from({ length: maxActivitiesVisible }, (_, k) => indexFirstDot + k),
    indexFirstDot + maxActivitiesVisible
  ];

  const dots = () => {
    if(!advancedNavigation) {
      return activities.map((activity, i) => {
        return {
          ...activity,
          active: !isAlternativeSection(activity) && activityIdSelected === activity?.id,
          index: i,
          onClick: () => {
            handleDotClick(i);
          },
          small: false
        };
      });
    }
    return indexesDotList
      .filter(i => i >= 0 && i <= activities.length - 1)
      .map(i => {
        const isShrunk = i === indexFirstDot - 1 || i === indexFirstDot + maxActivitiesVisible;

        return {
          active: !isAlternativeSection(activities[i])
            && activityIdSelected === activities[i]?.id,
          index: i,
          onClick: () => {
            handleDotClick(i);
            if (i === indexFirstDot + maxActivitiesVisible) {
              setIndexFirstDot(s => Math.min(s + 1, maxStart));
              return;
            }
            if (i === indexFirstDot - 1) {
              setIndexFirstDot(s => Math.max(s - 1, 0));
              return;
            }
          },
          small: isShrunk
        };
      });
  };

  const handleDotClick = useCallback((index: number) => {
    const activity = activities[index];
    if (isAlternativeSection(activity)) return;
    setActivityIdSelected(activity.id);
    syncUrlToActivity(activity);
  }, [activities, setActivityIdSelected, syncUrlToActivity]);

  useEffect(() => {
    if (!initialActiveLoId) return;
    setActivityIdSelected(initialActiveLoId);

    const targetIndex = activities.findIndex((activity) =>
      !isAlternativeSection(activity) && activity.id === initialActiveLoId
    );
    if (targetIndex < 0) return;

    const clampedMaxStart = Math.max(0, activities.length - maxActivitiesVisible);

    const start = Math.min(
      Math.max(targetIndex - Math.floor(maxActivitiesVisible / 2), 0),
      clampedMaxStart
    );
    setIndexFirstDot(start);
  }, [initialActiveLoId, activities, setActivityIdSelected]);

  
  useEffect(() => {
    if (!deepLinkLanding || landingHandled.current || !initialActiveLoId) return;
    const targetIndex = activities.findIndex((activity) =>
      !isAlternativeSection(activity) && activity.id === initialActiveLoId
    );
    if (targetIndex < 0) return;
    landingHandled.current = true;

    const target = activities[targetIndex];
    const targetTypology = isAlternativeSection(target)
      ? undefined
      : target?.learningObjectTypology;
    const isClassroomTarget = targetTypology === "DA_VIRTUAL_CLASS" || targetTypology === "DA_PHYSICAL_CLASS";

    setTimeout(() => {
      if (!isMounted.current) return;
      document.getElementById(`card-content-${initialActiveLoId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (isClassroomTarget) setBlinkId(initialActiveLoId);
    }, 350);
    setTimeout(() => {
      if (isMounted.current) setBlinkId(null);
    }, 2200);
  }, [deepLinkLanding, initialActiveLoId, activities]);

  useEffect(() => {
    setAdvancedNavigation && setAdvancedNavigation(activities?.length > 8);
  }, [activities.length]);

  const isCardActive = (index: number) => {
    const activity = activities[index];
    return !isAlternativeSection(activity) && activityIdSelected === activity.id;
  };
  const isActivityComplete = (index: number) => {
    const activity = activities[index];
    return !isAlternativeSection(activity) && activity.status === "C";
  };
  // if card index is above the active index, the card must be shown
  //const isCardVisible = (index: number) => index >= activeIndex; // nascondo le card precedenti a quella attiva
  const isCardVisible = (index: number) => {
    if (!advancedNavigation) return true;
    if (isCardActive(index)) return true;
    return index >= indexFirstDot && index < indexFirstDot + maxActivitiesVisible;
  };

  return (
    <NavigationWrapper>
      <Box
        position={ "absolute" }
        top={ advancedNavigation ? "unset" : "0" }
      >
        <DotColumn
          tabIndex={ -1 }
          role="tablist"
        >
          { dots()?.map((dot) => (
            <Stack
              key={ dot.index }
              tabIndex={ -1 }
              aria-selected={ isCardActive(dot.index) }
              role="tab"
              onClick={ () => dot.onClick() }
              sx={ {
                alignItems: "center",
                cursor: "pointer",
                height: "1.25rem",
                justifyContent: "center",
                width: "1.5rem",
                ...!isCardActive(dot.index) && {
                  "&:hover > span": {
                    background: theme.customColors.textPrimary
                  }
                }
              } }
            >
              <DotButton
                as="span"
                tabIndex={ -1 }
                $active={ isCardActive(dot.index) }
                $isComplete={ isActivityComplete(dot.index) }
                $isNavigationDot={ dot.small ?? null }
              />
            </Stack>
          )) }
        </DotColumn>

      </Box>

      <CardsColumn
        id={ `cards-column-${courseId}` }
        marginLeft={ "2.5rem" }
      >
        { activities?.map((activity, index) => (
          <CardSlot
            key={ index }
            className="cardSlot"

            sx={ {
              borderRadius: "0.5rem",
              ...isCardVisible(index) && {
                gridTemplateRows: "1fr",
                opacity: 1,
                pointerEvents: "auto"
              }
            } }
          >
            <CardSlotInner
              className="cardSlotInner"
            >
              { isAlternativeSection(activity) ? (
                <LearningObjectAlternativeSection
                  disabled={ selectionDisabled }
                  index={ index + 1 }
                  section={ activity }
                  showChoice
                />
              ) : (
                <CourseActivityCard
                  { ...activity }
                  activity={ activity }
                  index={ index + 1 }
                  realIndex={ index }
                  isActive={ isCardActive(index) }
                  isBlinking={ blinkId === activity.id }
                  handleActivityClick={ (i) => handleDotClick(i as number) }
                  madeAttempts={ undefined }
                />
              ) }
            </CardSlotInner>
          </CardSlot>
        )) }
      </CardsColumn>
    </NavigationWrapper>
  );
}
